import { createContext, useContext, useCallback, type ReactNode } from "react";
import { updateOwnProfile, uploadAvatar } from "../../../lib/expert_insights_api";
import type { ContributorProfile, UpdateProfileBody } from "../../../lib/expert_insights_api";
import { useUserContext } from "../../../context/UserContext";

export interface ExpertInsightsContextValue {
  profile: ContributorProfile | null;
  profileLoading: boolean;
  /** Updates the profile via the API and refreshes the cached value. */
  updateProfile: (body: UpdateProfileBody) => Promise<ContributorProfile>;
  /** Uploads a new avatar and refreshes the cached profile. */
  uploadProfileAvatar: (file: File) => Promise<ContributorProfile>;
}

const ExpertInsightsContext = createContext<ExpertInsightsContextValue | null>(null);

interface ExpertInsightsContextProviderProps {
  children: ReactNode;
}

export function ExpertInsightsContextProvider({ children }: ExpertInsightsContextProviderProps) {
  const { loading: profileLoading, profile, refreshProfile } = useUserContext();

  const updateProfile = useCallback(
    async (body: UpdateProfileBody): Promise<ContributorProfile> => {
      const updated = await updateOwnProfile(body);
      await refreshProfile();
      return updated;
    },
    [refreshProfile]
  );

  const uploadProfileAvatar = useCallback(
    async (file: File): Promise<ContributorProfile> => {
      const updated = await uploadAvatar(file);
      await refreshProfile();
      return updated;
    },
    [refreshProfile]
  );

  return (
    <ExpertInsightsContext.Provider
      value={{ profile, profileLoading, updateProfile, uploadProfileAvatar }}
    >
      {children}
    </ExpertInsightsContext.Provider>
  );
}

/** Consumes the ExpertInsightsContext; must be used inside ExpertInsightsContextProvider. */
export function useExpertInsightsContext(): ExpertInsightsContextValue {
  const ctx = useContext(ExpertInsightsContext);
  if (!ctx) throw new Error("useExpertInsightsContext used outside ExpertInsightsContextProvider");
  return ctx;
}
